/**
 * A "Smart Controller" wrapper for VerticalNavPanel that implements specific IDE-like navigation patterns.
 * 
 * Key Behaviors:
 * 1. Peek vs. Commit: Clicking a Chevron "peeks" (multi-expand). Clicking a Row "commits" (accordion collapse).
 * 2. Auto-Redirect: Clicking a Parent Folder automatically selects its first child.
 * 3. Context Switching: Selecting a new parent automatically collapses all other open folders.
 * 
 * CRITICAL REQUIREMENTS: 
 * - Component is FULLY CONTROLLED for selection - parent owns selectedId state
 * - All Parent Items in `items` MUST have `isSelectable: true` (or undefined) for auto-redirect to work
 * - All Parent Items MUST have children array (even single-child scenarios) to trigger auto-redirect
 */

import { useState, useEffect } from "react";
import { VerticalNavPanel, TabItem } from "./VerticalNavPanel";

export interface AutoSelectVerticalNavProps {
  title: string;
  titleClassName?: string;
  headerClassName?: string;
  headerAction?: React.ReactNode;
  items: ReadonlyArray<TabItem>;
  selectedId?: string;
  onSelectionChange?: (id: string) => void;
  className?: string;
  rowClassName?: string;
  showTitleOnHover?: boolean;
  listContainerClassName?: string;
  rowHeaderClassName?: string;
}

export function AutoSelectVerticalNav({
  title,
  titleClassName,
  headerClassName,
  headerAction,
  items,
  selectedId,
  onSelectionChange,
  className,
  rowClassName,
  showTitleOnHover,
  listContainerClassName,
  rowHeaderClassName,
}: AutoSelectVerticalNavProps) {
  
  // Internal state: manages which folders are visually expanded
  // Syncs with selectedId to implement accordion behavior
  const [expandedIds, setExpandedIds] = useState<ReadonlyArray<string>>([]);

  // Derive expansion state from selectedId prop
  // When selection changes, auto-expand the parent and collapse others (accordion)
  useEffect(() => {
    if (!selectedId) {
      setExpandedIds([]);
      return;
    }

    // Find the parent of the currently selected item
    const parentId = items.find(parent => 
      selectedId === parent.id || 
      parent.children?.some(child => child.id === selectedId)
    )?.id;

    if (parentId) {
      // Accordion: only expand the parent containing the selection
      setExpandedIds([parentId]);
    } else {
      setExpandedIds([]);
    }
  }, [selectedId, items]);

  // PEEK: Chevron click toggles expansion without changing selection
  // Allows multi-expand to compare folder contents
  const handleToggle = (id: string) => {
    setExpandedIds((prev) => 
      prev.includes(id) 
        ? prev.filter((expandedId) => expandedId !== id)
        : [...prev, id]
    );
  };

  // COMMIT: Body click selects and enforces accordion behavior
  const handleSelect = (clickedId: string) => {
    
    // Parent clicked: Auto-redirect to first child
    const parentFolder = items.find((item) => item.id === clickedId);

    if (parentFolder) {
      const firstChild = parentFolder.children?.[0];
      
      if (firstChild) {
        onSelectionChange?.(firstChild.id);
        setExpandedIds([parentFolder.id]);
      } else {
        // Fallback: parent with no children (should not happen with proper data)
        onSelectionChange?.(clickedId);
        setExpandedIds([clickedId]);
      }
      return;
    }

    // Child clicked: Select child and collapse other folders
    const parentOfClickedChild = items.find(p => 
      p.children?.some(c => c.id === clickedId)
    );

    if (parentOfClickedChild) {
      onSelectionChange?.(clickedId);
      setExpandedIds([parentOfClickedChild.id]);
    } else {
      // Fallback: orphaned child (should not happen with proper data)
      onSelectionChange?.(clickedId);
    }
  };

  return (
    <VerticalNavPanel
      title={title}
      titleClassName={titleClassName}
      headerClassName={headerClassName}
      headerAction={headerAction}
      items={items}
      selectedId={selectedId}
      expandedIds={expandedIds}
      onSelect={handleSelect}
      onToggle={handleToggle}
      className={className}
      rowClassName={rowClassName}
      showTitleOnHover={showTitleOnHover}
      listContainerClassName={listContainerClassName}
      rowHeaderClassName={rowHeaderClassName}
    />
  );
}
